import { useEffect, useState, type KeyboardEvent } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button, Dialog, Field, FieldLabel, Input } from "@akuna/ui";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { useSaveStayPlan, type StayPlan } from "@/hooks/useStayPlans";
import type { StayPlanKind, StayPlanModule } from "@/lib/daycare-hotel/stayPlans";

type UsageRule = "per_week" | "per_month";

const usageRules: UsageRule[] = ["per_week", "per_month"];

export default function StayPlanDialog({
  open,
  onOpenChange,
  kind,
  allowedModules,
  plan,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: StayPlanKind;
  allowedModules: StayPlanModule[];
  plan: StayPlan | null;
}) {
  const { t } = useTranslation("daycareHotel");
  const [module, setModule] = useState<StayPlanModule>(allowedModules[0] ?? "daycare");
  const save = useSaveStayPlan(module);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [priceCents, setPriceCents] = useState(0);
  const [days, setDays] = useState("1");
  const [rule, setRule] = useState<UsageRule>("per_week");
  const [limit, setLimit] = useState("1");
  const [weekInterval, setWeekInterval] = useState<1 | 2>(1);
  const [priceWithPlan, setPriceWithPlan] = useState("");
  const [priceWithPlanCents, setPriceWithPlanCents] = useState(0);
  const [priceWithPlanTouched, setPriceWithPlanTouched] = useState(false);
  // Só aparece pra criar um pacote novo com os dois módulos liberados;
  // editando, o módulo já está fixado pelo registro existente.
  const showModulePicker = !plan && allowedModules.length > 1;

  useEffect(() => {
    if (!open) return;

    setModule(plan?.module ?? allowedModules[0] ?? "daycare");
    setName(plan?.name ?? "");
    const initialPriceCents = plan ? Math.round(plan.price * 100) : 0;
    setPriceCents(initialPriceCents);
    setPrice(initialPriceCents ? (initialPriceCents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");
    const initialPriceWithPlanCents = plan?.priceWithOtherModulePlan != null ? Math.round(plan.priceWithOtherModulePlan * 100) : 0;
    setPriceWithPlanCents(initialPriceWithPlanCents);
    setPriceWithPlan(initialPriceWithPlanCents ? (initialPriceWithPlanCents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");
    setPriceWithPlanTouched(plan?.priceWithOtherModulePlan != null);
    setDays(String(plan?.includedDays ?? 1));
    setRule(plan?.usageRule === "per_month" ? "per_month" : "per_week");
    setLimit(String(plan?.usageLimit ?? 1));
    setWeekInterval(plan?.weekInterval ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  const hasValidRequiredFields = name.trim().length > 0
    && priceCents > 0
    && (kind === "day_package"
      ? Number.isInteger(Number(days)) && Number(days) > 0
      : Number.isInteger(Number(limit)) && Number(limit) > 0);

  const makeCentsKeyDownHandler = (cents: number, setCents: (value: number) => void, setDisplay: (value: string) => void) =>
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.key.startsWith("Arrow") || ["Tab", "Enter", "Escape"].includes(event.key)) return;
      if (!/^\d$/.test(event.key) && event.key !== "Backspace" && event.key !== "Delete") {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      const nextCents = event.key === "Backspace" || event.key === "Delete"
        ? Math.floor(cents / 10)
        : Math.min(cents * 10 + Number(event.key), 999999999);
      setCents(nextCents);
      setDisplay(nextCents ? (nextCents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");
    };

  const basePriceKeyDownHandler = makeCentsKeyDownHandler(priceCents, setPriceCents, setPrice);
  const handlePriceKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    basePriceKeyDownHandler(event);
    const isBackspaceOrDelete = event.key === "Backspace" || event.key === "Delete";
    if (priceWithPlanTouched || (!isBackspaceOrDelete && !/^\d$/.test(event.key))) return;

    const nextCents = isBackspaceOrDelete
      ? Math.floor(priceCents / 10)
      : Math.min(priceCents * 10 + Number(event.key), 999999999);
    setPriceWithPlanCents(nextCents);
    setPriceWithPlan(nextCents ? (nextCents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");
  };
  const basePriceWithPlanKeyDownHandler = makeCentsKeyDownHandler(priceWithPlanCents, setPriceWithPlanCents, setPriceWithPlan);
  const handlePriceWithPlanKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    setPriceWithPlanTouched(true);
    basePriceWithPlanKeyDownHandler(event);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasValidRequiredFields) return;

    try {
      await save.mutateAsync(
        kind === "day_package"
          ? {
              id: plan?.id,
              module,
              name,
              price: priceCents / 100,
              kind,
              isActive: plan?.isActive ?? true,
              includedDays: Number(days),
              usageRule: null,
              usageLimit: null,
              weekInterval: 1,
              // Em branco significa "cobra o mesmo dos dois"; zero é preço.
              priceWithOtherModulePlan: priceWithPlanCents > 0 ? priceWithPlanCents / 100 : null,
            }
          : {
              id: plan?.id,
              module,
              name,
              price: priceCents / 100,
              kind,
              isActive: plan?.isActive ?? true,
              includedDays: null,
              usageRule: rule,
              usageLimit: Number(limit),
              weekInterval: rule === "per_week" && module === "daycare" ? weekInterval : 1,
              priceWithOtherModulePlan: null,
            },
      );
      onOpenChange(false);
      toast({ title: t(kind === "day_package" ? "packages.saved" : "plans.saved") });
    } catch {
      toast({ variant: "destructive", title: t(kind === "day_package" ? "packages.saveFailed" : "plans.saveFailed") });
    }
  };

  const renderPriceField = (
    label: string,
    value: string,
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void,
  ) => (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-center rounded-md border border-input bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
        <span className="mr-1 text-sm text-muted-foreground">R$</span>
        <Input aria-label={label} inputMode="numeric" value={value} onKeyDown={onKeyDown} onChange={() => {}} placeholder="0,00" className="border-0 px-0 shadow-none focus-visible:ring-0" />
      </div>
    </Field>
  );

  return (
    <Dialog
      className="akuna-dialog--wide akuna-dialog--compact"
      closeLabel={t("common.cancel")}
      icon={
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-akuna-orange">
          <SlidersHorizontal aria-hidden="true" size={22} />
        </span>
      }
      header={<span>{t(kind === "day_package" ? "packages.badge" : "plans.daycareBadge")}</span>}
      open={open}
      onOpenChange={onOpenChange}
      title={plan
        ? t(kind === "day_package" ? "packages.edit" : "plans.edit")
        : t(kind === "day_package" ? "packages.new" : "plans.new")}
      mode="adaptive"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="stay-plan-form" disabled={save.isPending || !hasValidRequiredFields}>
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <form id="stay-plan-form" data-testid="stay-plan-form" onSubmit={submit} className="grid gap-5">
        {showModulePicker ? (
          <Field>
            <FieldLabel>{t("plans.type")}</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              {allowedModules.map((value) => {
                const selected = module === value;
                const label = value === "hotel" ? "plans.report.moduleHotel" : "plans.report.moduleDaycare";
                const selectedTone = value === "hotel"
                  ? "border-purple-300 bg-purple-50 text-purple-700"
                  : "border-orange-300 bg-orange-50 text-orange-700";

                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setModule(value)}
                    className={`rounded-xl border px-3 py-3 text-left text-sm font-semibold transition-colors ${selected ? selectedTone : "border-border bg-card text-muted-foreground hover:bg-muted"}`}
                  >
                    <span className="block">{t(label)}</span>
                  </button>
                );
              })}
            </div>
          </Field>
        ) : null}

        <div data-testid="daycare-plan-primary-fields" className="grid gap-5 sm:grid-cols-2">
          <Field>
            <FieldLabel>{t("plans.name")}</FieldLabel>
            <Input autoFocus={!plan} value={name} onChange={(event) => setName(event.target.value)} />
          </Field>

          {kind === "day_package" ? (
            <Field>
              <FieldLabel>{t("plans.includedDays")}</FieldLabel>
              <Input type="number" min="1" value={days} onChange={(event) => setDays(event.target.value)} />
            </Field>
          ) : (
            renderPriceField(t("plans.price"), price, handlePriceKeyDown)
          )}
        </div>

        {kind === "day_package" ? (
          <div className="grid gap-5 sm:grid-cols-2">
            {renderPriceField(t("plans.priceStandard"), price, handlePriceKeyDown)}
            {renderPriceField(t("plans.priceWithActivePlan"), priceWithPlan, handlePriceWithPlanKeyDown)}
          </div>
        ) : (
          <Field>
            <FieldLabel>{t("plans.usageRule")}</FieldLabel>
            <div data-testid="plan-usage-rules" className="grid gap-2 sm:grid-cols-2">
              {usageRules.map((value) => {
                const selected = rule === value;

                return (
                  <div
                    key={value}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    onClick={() => setRule(value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setRule(value);
                      }
                    }}
                    data-testid={`plan-usage-rule-${value}`}
                    className={`flex flex-col gap-3 rounded-xl border px-3 py-3 text-left text-sm font-semibold transition-colors ${selected ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card text-muted-foreground hover:bg-muted"}`}
                  >
                    <span className="block">
                      <span className="block leading-tight">{t(`plans.ruleOptions.${value}`)}</span>
                      <span className="mt-1 block text-xs font-normal">{t(`plans.ruleHints.${value}`)}</span>
                    </span>

                    {selected ? (
                      <Field id={`plan-usage-limit-${value}`} onClick={(event) => event.stopPropagation()}>
                        <FieldLabel>
                          {t(value === "per_week" ? "plans.weeklyLimit" : "plans.monthlyLimit")}
                        </FieldLabel>
                        <Input
                          type="number"
                          min="1"
                          value={limit}
                          onChange={(event) => setLimit(event.target.value)}
                        />
                      </Field>
                    ) : null}
                    {selected && value === "per_week" && module === "daycare" ? (
                      <Field onClick={(event) => event.stopPropagation()}>
                        <FieldLabel>{t("plans.frequencyLabel")}</FieldLabel>
                        <div role="radiogroup" aria-label={t("plans.frequencyLabel")} className="flex flex-wrap gap-1.5">
                          {([1, 2] as const).map((interval) => (
                            <button
                              key={interval}
                              type="button"
                              role="radio"
                              aria-checked={weekInterval === interval}
                              onClick={() => setWeekInterval(interval)}
                              className={`rounded-full border px-3 py-1 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${weekInterval === interval ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-muted"}`}
                            >
                              {t(interval === 2 ? "plans.frequency.biweekly" : "plans.frequency.weekly")}
                            </button>
                          ))}
                        </div>
                        <span className="text-xs font-normal text-muted-foreground">{t("plans.frequencyHint")}</span>
                      </Field>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </Field>
        )}
      </form>
    </Dialog>
  );
}
